import userService from "@/endpoints/userService";
import {
  createSegmentAfterValidation,
  updateStatus,
} from "@/store/slices/segments/segments.thunk";
import type { AppDispatch, RootState } from "@/store/store";
import { dequal } from "dequal";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  clearPendingValidationSegments,
  setPendingSelectionIfChanged,
} from "@/store/slices/pendingSelection/pendingSelectionSlice";

/**
 * Module-level flag — set to `true` after the first validation is triggered.
 * Persists for the entire JS session (survives component remounts / route
 * changes) so the validate API is called **at most once per app load**.
 */
let validationTriggeredThisSession = false;
import ModalWithoutHeaderAndFooterAtCenter from "@/components/common/ModalWithoutHeaderAndFooterAtCenter/ModalWithoutHeaderAndFooterAtCenter";
import ModalHeader from "@/components/common/ModalWithoutHeaderAndFooter/components/ModalHeader/ModalHeader";
import { IoWarningOutline } from "react-icons/io5";
import TriStateCheckbox from "@/components/common/TriStateCheckbox/TriStateCheckbox";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import CONFIG from "@/config";

const ValidateSegment = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<
    "FILTER_EXPIRED_LIST" | "NEW_SHARED_SEGMENTS" | ""
  >("");
  const [expiredOrDetachedSegmentList, setExpiredOrDetachedSegmentList] =
    useState<string[]>([]);
  const [validSegmentDetailToAdd, setValidSegmentDetailToAdd] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);

  const { ids: userSegments, entities: userSegmentsEntities } = useSelector(
    (state: RootState) => ({
      ids: state.activeGroup.segments.segments.ids,
      entities: state.activeGroup.segments.segments.entities,
    }),
    dequal,
  );

  const userSelectedMarket = useSelector(
    (state: RootState) =>
      state.userSelection.markets?.length > 0
        ? state.userSelection.markets[0]
        : null,
    dequal,
  ) as number;

  const allMarketDetails = useSelector(
    (state: RootState) => state.activeGroup.markets,
    dequal,
  );

  /**
   * Segments already committed to the URL by the selectionListener.
   * Used in `handleCreateSegment` instead of reading from `location.search`
   * so we always work with the validated, pruned selection state.
   */
  const currentSegments = useSelector(
    (state: RootState) => state.userSelection.segments ?? [],
    dequal,
  );

  /**
   * Segment IDs captured by `FetchFiltersAndSegments` during the initial
   * page-load fetch: these were in the URL but NOT in the user's loaded
   * segment library.  Non-empty → trigger the validate API.
   * Cleared immediately after the API call is initiated.
   */
  const pendingValidationSegments = useSelector(
    (state: RootState) => state.pendingSelection.pendingValidationSegments,
    dequal,
  );

  const userSelectedMarketCode: string[] = useMemo(
    () =>
      [userSelectedMarket]
        ?.map((marketId: number) => {
          const currMarket = allMarketDetails.markets.entities[marketId];
          return currMarket?.identity;
        })
        .filter((market): market is string => !!market),
    [userSelectedMarket, allMarketDetails.markets.entities],
  );

  const location = useLocation();

  /**
   * Fires when `FetchFiltersAndSegments` has detected unknown segments in the
   * initial URL (stored in `pendingValidationSegments`) and the market is
   * ready in `userSelection`.
   *
   * Replaces the old `location.search` comparison: the listener prunes the
   * URL BEFORE `ValidateSegment` would have had a chance to read unknown IDs,
   * so we capture them earlier (in `FetchFiltersAndSegments`) and read them
   * from Redux here.
   */
  useEffect(() => {
    if (
      !validationTriggeredThisSession &&
      location.pathname?.includes("/segment") &&
      pendingValidationSegments.length > 0 &&
      userSelectedMarket
    ) {
      // Mark as done at module level — prevents re-triggering on remounts,
      // market switches, or any other re-renders for the rest of the session.
      validationTriggeredThisSession = true;

      // Consume the Redux field immediately so no other effect instance fires.
      dispatch(clearPendingValidationSegments());

      // Double-check: only pass IDs that are genuinely NOT in the currently
      // loaded segment library (guards against any string/number type mismatch
      // that may have slipped through the FetchFiltersAndSegments filter).
      const loadedIds = new Set<number>(
        (userSegments as any[]).map(Number),
      );
      const unknownOnly = pendingValidationSegments.filter(
        (id) => !loadedIds.has(Number(id)),
      );

      if (unknownOnly.length > 0) {
        validateSegments(unknownOnly);
      }
    }
  }, [pendingValidationSegments, userSelectedMarket]);

  async function validateSegments(invalidSegments: number[]) {
    try {
      const response = (await userService.segments.validateSegment(
        [userSelectedMarket],
        invalidSegments,
      )) as any[];

      const validSegmentsIds: { id: number; name: string }[] = [];
      const expiredFilters: string[] = [];
      response?.forEach((segment: any) => {
        if (
          segment?.detachedFilters?.length > 0 ||
          segment?.expiredFilters?.length > 0
        ) {
          segment?.filters?.forEach((filter: any) => {
            if (
              segment?.detachedFilters?.includes(filter?.filterName) ||
              segment?.expiredFilters?.includes(filter?.filterName)
            ) {
              filter?.filterOptions?.forEach((filterOption: any) => {
                if (
                  !expiredFilters?.includes(
                    `${filter?.filterName}/${filterOption?.filterOptionName}`,
                  )
                ) {
                  expiredFilters?.push(
                    `${filter?.filterName}/${filterOption?.filterOptionName}`,
                  );
                }
              });
            }
          });
        } else if (
          segment?.isApplicableForUser &&
          segment?.detachedFilters?.length === 0 &&
          segment?.expiredFilters?.length === 0
        ) {
          validSegmentsIds?.push({
            id: segment?.segmentId,
            name: segment?.segmentName,
          });
        }
      });
      if (expiredFilters?.length > 0) {
        setCurrentStep("FILTER_EXPIRED_LIST");
        setOpen(true);
      } else if (validSegmentsIds?.length > 0) {
        setCurrentStep("NEW_SHARED_SEGMENTS");
        setOpen(true);
      } else {
        setCurrentStep("");
        setOpen(false);
      }
      setValidSegmentDetailToAdd(validSegmentsIds);
      setExpiredOrDetachedSegmentList(expiredFilters);
    } catch (err: unknown) {
      return { error: "Something went wrong", errorCode: 500 };
    }
  }

  const segmentMaxLimit = Number(CONFIG.segments?.maxAllowedSegments) || 200;
  const activeSegmentsMaxLimit = Number(CONFIG.segments?.maxAllowedActiveSegments) || 20;
  const segmentsMaxLimitReached =
    selectedSegments?.length + userSegments?.length > segmentMaxLimit;

  async function handleCreateSegment() {
    if (segmentsMaxLimitReached) {
      setOpen(false);
    } else if (selectedSegments.length > 0) {
      dispatch(
        createSegmentAfterValidation({
          marketIds: [userSelectedMarket],
          marketCodes: userSelectedMarketCode,
          segmentIds: selectedSegments,
        }),
      ).then(async (response) => {
        if (
          response.payload &&
          typeof response.payload !== "string" &&
          response.payload.segments
        ) {
          // Use the committed `userSelection.segments` (already pruned and
          // clean) instead of reading from `location.search`.  By the time
          // this callback runs, the listener has committed the clean segments
          // to the store, so `currentSegments` contains the correct set.
          const segmentsFromStore = currentSegments.filter(
            (segmentId: number) => {
              const entity = userSegmentsEntities[segmentId] as any;
              return (
                entity?.segmentMarkets?.length > 0 &&
                entity?.segmentMarkets?.[0] !== "Not Applicable"
              );
            },
          );

          setOpen(false);
          const totalSegments =
            segmentsFromStore.length + selectedSegments.length;

          if (totalSegments > 20) {
            const keepCount = 20 - selectedSegments.length;
            await dispatch(
              updateStatus(
                segmentsFromStore.slice(keepCount).map((id) => ({
                  segmentId: id,
                  isActive: false,
                })),
              ),
            );
            dispatch(
              setPendingSelectionIfChanged({
                segments: [
                  ...new Set([
                    ...segmentsFromStore.slice(0, keepCount),
                    ...selectedSegments,
                  ]),
                ],
              } as any),
            );
          } else {
            dispatch(
              setPendingSelectionIfChanged({
                segments: [
                  ...new Set([...segmentsFromStore, ...selectedSegments]),
                ],
              } as any),
            );
          }
        }
      });
    } else {
      setOpen(false);
    }
  }

  return (
    <ModalWithoutHeaderAndFooterAtCenter
      open={open}
      onClose={() => {
        if (currentStep === "FILTER_EXPIRED_LIST") {
          if (validSegmentDetailToAdd?.length > 0) {
            setCurrentStep("NEW_SHARED_SEGMENTS");
          } else {
            setOpen(false);
          }
        } else {
          setOpen(false);
        }
      }}
    >
      {currentStep === "FILTER_EXPIRED_LIST" && (
        <ModalHeader
          title={"Limited access"}
          subTitle={"You may not have access to certain filters."}
          onClose={() => {
            if (validSegmentDetailToAdd?.length > 0) {
              setCurrentStep("NEW_SHARED_SEGMENTS");
            } else {
              setOpen(false);
            }
          }}
          icon={
            <div className="bg-[#f1e6bf] rounded-[50%] p-[10px]">
              <IoWarningOutline />
            </div>
          }
        />
      )}
      {currentStep === "NEW_SHARED_SEGMENTS" && (
        <ModalHeader
          title={
            segmentsMaxLimitReached ? "Limit reached" : "New shared segments"
          }
          subTitle={
            segmentsMaxLimitReached
              ? "You have reached the maximum segments limit."
              : "You have been shared new segments."
          }
          onClose={() => setOpen(false)}
          icon={
            <div className="bg-[#f1e6bf] rounded-[50%] p-[10px]">
              <IoWarningOutline />
            </div>
          }
        />
      )}

      <div className="p-6 pb-1 max-h-[480px] overflow-auto ">
        {currentStep === "FILTER_EXPIRED_LIST" && (
          <div>
            <div className="font-normal text-[12px] text-[#475467] pb-4">
              The following filters are unavailable in your account{" "}
            </div>
            <ul className="list-disc ml-6 max-w-[520px]">
              {expiredOrDetachedSegmentList?.map((item: string) => (
                <li className="mb-[2px]" key={item}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {currentStep === "NEW_SHARED_SEGMENTS" && (
          <div className="max-w-[520px]">
            <>
              {userSegments?.length >= segmentMaxLimit ? (
                <>
                  <div className="font-normal text-[12px] text-[#475467] pb-4">
                    You have used{" "}
                    <span style={{ fontWeight: "500" }}>
                      {userSegments?.length} out of {segmentMaxLimit}
                    </span>{" "}
                    segments.You can't create from the shared segments below.
                  </div>
                </>
              ) : userSegments?.length + validSegmentDetailToAdd?.length >=
                segmentMaxLimit ? (
                <div className="font-normal text-[12px] text-[#475467] pb-4">
                  You have used{" "}
                  <span style={{ fontWeight: "500" }}>
                    {userSegments?.length} out of {segmentMaxLimit}
                  </span>{" "}
                  segments.You can create{" "}
                  <span style={{ fontWeight: "500" }}>
                    {segmentMaxLimit - userSegments?.length}
                  </span>{" "}
                  more from the shared segments below.
                </div>
              ) : (
                <div className="font-normal text-[12px] text-[#475467] pb-4">
                  Would you like us to set up the shared segments in your
                  account? Please choose from the following options to proceed:
                </div>
              )}
            </>
            <ul>
              {validSegmentDetailToAdd?.map(
                (item: { id: number; name: string }) => (
                  <li key={item.id} className="flex gap-2">
                    <TriStateCheckbox
                      size="small"
                      state={
                        selectedSegments?.includes(item?.id)
                          ? "checked"
                          : "unchecked"
                      }
                      enableTriState={false}
                      onChange={() => {
                        if (selectedSegments?.includes(item?.id)) {
                          const updatedIds = selectedSegments?.filter(
                            (id) => id !== item?.id,
                          );
                          setSelectedSegments(updatedIds);
                          return;
                        }
                        if (segmentsMaxLimitReached) {
                          return;
                        }
                        if (
                          userSegments?.length + selectedSegments?.length >=
                          segmentMaxLimit
                        ) {
                          return;
                        }
                        setSelectedSegments([...selectedSegments, item?.id]);
                      }}
                    />
                    <div>{item?.name}</div>
                  </li>
                ),
              )}
            </ul>
          </div>
        )}
        <div className="py-4 font-normal text-[12px] text-[#475467]">
          Contact your customer success representative for assistance.
        </div>
        {(segmentsMaxLimitReached ||
          userSegments?.length >= segmentMaxLimit) && (
            <div className="p-2 rounded-[4px] mb-4 flex border border-neutral-200 gap-2 font-normal text-[12px] items-center text-[#475467]">
              <HiOutlineInformationCircle className="text-[20px] text-[#bfab34]" />
              <div>We recommend deleting some existing segments.</div>
            </div>
          )}
      </div>
      {currentStep === "FILTER_EXPIRED_LIST" && (
        <div className="py-4 pt-4 px-6 border-t border-neutral-200">
          <button
            onClick={() => {
              if (validSegmentDetailToAdd?.length > 0) {
                setCurrentStep("NEW_SHARED_SEGMENTS");
              } else {
                setOpen(false);
              }
            }}
            className="rounded-[2px] cursor-pointer bg-[#0062ff] w-full p-[14px] mb-2 text-semibold text-white transition hover:bg-[#004ecc]"
          >
            Understood
          </button>
        </div>
      )}
      {currentStep === "NEW_SHARED_SEGMENTS" && (
        <div className="py-4 pt-4 px-6 border-t border-neutral-200">
          <button
            onClick={handleCreateSegment}
            className="rounded-[2px] cursor-pointer bg-[#0062ff] w-full p-[14px] mb-2 text-semibold text-white transition hover:bg-[#004ecc]"
          >
            Proceed
          </button>
        </div>
      )}
    </ModalWithoutHeaderAndFooterAtCenter>
  );
};

export default ValidateSegment;
